import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Play, X, Image, Film } from 'lucide-react';

const MediaGallery = ({ media = [], projectTitle, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const videoRef = useRef(null);

  const minSwipeDistance = 50;
  const mediaLength = media.length;

  // Keep index valid if media changes
  useEffect(() => {
    if (mediaLength === 0) {
      setCurrentIndex(0);
      return;
    }
    if (currentIndex > mediaLength - 1) {
      setCurrentIndex(0);
    }
  }, [mediaLength, currentIndex]);

  const goToNext = useCallback(() => {
    if (mediaLength === 0) {
      return;
    }
    setCurrentIndex((prev) => (prev + 1) % mediaLength);
  }, [mediaLength]);

  const goToPrevious = useCallback(() => {
    if (mediaLength === 0) {
      return;
    }
    setCurrentIndex((prev) => (prev - 1 + mediaLength) % mediaLength);
  }, [mediaLength]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext, onClose]);

  // Pause video when switching slides
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, [currentIndex]);

  const currentMedia = mediaLength > 0 ? media[currentIndex] : null;

  // Now it's safe to return conditionally because hooks already ran
  if (!currentMedia) {
    return (
      <div className="flex flex-col h-full bg-zinc-950">
        <div className="relative flex-1 flex items-center justify-center">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-10 h-10 bg-black/50 hover:bg-orange-500 flex items-center justify-center text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="text-white text-center px-6">
            <p className="text-lg font-bold">No media available</p>
            <p className="text-zinc-400 mt-2">This project doesn’t have photos/videos yet.</p>
          </div>
        </div>
      </div>
    );
  }

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Main Media Display */}
      <div 
        className="relative flex-1 flex items-center justify-center overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 bg-black/50 hover:bg-orange-500 flex items-center justify-center text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Project title and counter */}
        <div className="absolute top-4 left-4 z-20 flex items-center gap-3">
          <div className="bg-black/50 px-3 py-1.5 text-white text-sm font-bold">
            {currentIndex + 1} / {media.length}
          </div>
          {projectTitle && (
            <div className="hidden sm:block bg-black/50 px-3 py-1.5 text-white text-sm font-bold uppercase tracking-wide max-w-xs truncate">
              {projectTitle}
            </div>
          )}
        </div>

        {/* Navigation Arrows */}
        {media.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 z-10 w-12 h-12 bg-black/50 hover:bg-orange-500 flex items-center justify-center text-white transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 z-10 w-12 h-12 bg-black/50 hover:bg-orange-500 flex items-center justify-center text-white transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Media Content */}
        <div className="w-full h-full flex items-center justify-center p-4">
          {currentMedia.type === 'image' ? (
            <img
              src={currentMedia.url}
              alt={currentMedia.caption || 'Project image'}
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <video
              ref={videoRef}
              src={currentMedia.url}
              controls
              className="max-w-full max-h-full"
              playsInline
            >
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      </div>

      {/* Caption */}
      {currentMedia.caption && (
        <div className="bg-zinc-900 px-6 py-3 text-center">
          <p className="text-zinc-300 text-sm">{currentMedia.caption}</p>
        </div>
      )}

      {/* Thumbnail Strip */}
      <div className="bg-zinc-900 border-t border-zinc-800 p-4">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {media.map((item, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`relative flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 overflow-hidden border-2 transition-all ${
                index === currentIndex
                  ? 'border-orange-500'
                  : 'border-transparent hover:border-zinc-600'
              }`}
            >
              {item.type === 'image' ? (
                <img
                  src={item.url}
                  alt={item.caption || `Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-zinc-800 flex items-center justify-center relative">
                  <video
                    src={item.url}
                    className="w-full h-full object-cover"
                    muted
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <Play className="w-6 h-6 text-white" fill="white" />
                  </div>
                </div>
              )}
              {/* Media type indicator */}
              <div className="absolute bottom-1 right-1 w-5 h-5 bg-black/70 rounded flex items-center justify-center">
                {item.type === 'image' ? (
                  <Image className="w-3 h-3 text-white" />
                ) : (
                  <Film className="w-3 h-3 text-white" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MediaGallery;
